export {};

type CookieRecord = {
  id: number;
  cookie_name: string;
  baker_display_name: string;
  photo_data_url: string;
  gluten_free: boolean;
  includes_extra_mixins: boolean;
  dairy_free: boolean;
  other_notes: string;
  vote_count: number;
};

type VoteState = {
  overall: number;
  appearance: number;
  texture: number;
  flavor: number;
  superlatives: string[];
  updated_at: string;
};

type WinnerEntry = {
  cookie_name: string;
  baker_display_name: string;
  photo_data_url?: string;
  vote_count?: number;
  score?: number;
};

type ResultsPayload = {
  results: Array<{
    id: number;
    cookie_name: string;
    baker_display_name: string;
    photo_data_url?: string;
    other_notes?: string;
    vote_count: number;
    overall_avg: number;
    appearance_avg: number;
    texture_avg: number;
    flavor_avg: number;
  }>;
  category_winners: {
    overall: WinnerEntry[];
    appearance: WinnerEntry[];
    texture: WinnerEntry[];
    flavor: WinnerEntry[];
  };
  superlative_winners: Record<string, WinnerEntry[]>;
  generated_at: string;
};

type AdminVoteRow = {
  id: number;
  voter_id: string;
  cookie_id: number;
  cookie_name: string;
  baker_display_name: string;
  overall: number;
  appearance: number;
  texture: number;
  flavor: number;
  updated_at: string;
};

type AdminOverviewPayload = {
  settings: {
    allow_submissions: boolean;
    allow_voting: boolean;
  };
  cookies: CookieRecord[];
  votes: AdminVoteRow[];
};

declare global {
  interface Window {
    __APP_CONFIG__?: {
      showVoterId?: boolean;
    };
  }
}

const SUPERLATIVES = [
  { key: 'best_ratio', label: 'Best chocolate to dough ratio' },
  { key: 'best_dunk', label: 'Best to dunk in milk' },
  { key: 'most_unique', label: 'Most unique' },
  { key: 'nostalgia_award', label: 'Nostalgia award' }
] as const;

const ROUTES = {
  home: '/',
  submit: '/submit',
  vote: '/vote',
  results: '/results',
  admin: '/admin',
  qr: '/qr',
  qrSubmit: '/qr/submit',
  qrVote: '/qr/vote'
} as const;

const storage = {
  voterId: 'cookieparty:voterId',
  drafts: 'cookieparty:drafts',
  resultsPassphrase: 'cookieparty:resultsPassphrase'
} as const;

const showVoterId = Boolean(window.__APP_CONFIG__?.showVoterId);

function byId<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function appRoot(): HTMLElement | null {
  return byId('app');
}

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function route(): string {
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  if (pathname === ROUTES.submit) return ROUTES.submit;
  if (pathname === ROUTES.vote) return ROUTES.vote;
  if (pathname === ROUTES.results) return ROUTES.results;
  if (pathname === ROUTES.admin) return ROUTES.admin;
  if (pathname === ROUTES.qr) return ROUTES.qr;
  if (pathname === ROUTES.qrSubmit) return ROUTES.qrSubmit;
  if (pathname === ROUTES.qrVote) return ROUTES.qrVote;
  return ROUTES.home;
}

function setPageMode(mode: 'default' | 'print-qr'): void {
  const main = document.querySelector('main');
  if (!main) return;
  main.classList.remove('print-qr');
  main.classList.remove('tv-mode');
  if (mode === 'print-qr') {
    main.classList.add('print-qr');
  }
}

function navLinks(): string {
  const links = '<a class="btn btn-ghost cta" href="/">Home</a>'
    + '<a class="btn btn-ghost cta" href="/submit">Submit Cookie</a>'
    + '<a class="btn btn-ghost cta" href="/vote">Vote</a>'
    + '<a class="btn btn-ghost cta" href="/admin">Admin</a>'
    + '<a class="btn btn-ghost cta" href="/qr">QR Codes</a>';

  return '<nav class="nav-wrap">'
    + '<div class="link-row nav-desktop">' + links + '</div>'
    + '<div class="nav-mobile">'
    + '<button type="button" class="btn btn-ghost nav-toggle" aria-expanded="false">Menu</button>'
    + '<div class="nav-mobile-panel">' + links + '</div>'
    + '</div>'
    + '</nav>';
}

function wireNavMenu(): void {
  document.querySelectorAll<HTMLButtonElement>('.nav-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel = btn.parentElement?.querySelector<HTMLElement>('.nav-mobile-panel');
      if (!panel) return;
      const open = panel.classList.toggle('open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });
}

async function api(path: string, options?: RequestInit): Promise<any> {
  const res = await fetch(path, options);
  let payload: any = {};
  try {
    payload = await res.json();
  } catch {
    payload = {};
  }

  if (!res.ok) {
    const message = payload.error || 'Request failed';
    throw new Error(message);
  }

  return payload;
}

function getVoterId(): string {
  const existing = localStorage.getItem(storage.voterId);
  if (existing) return existing;

  const generated = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : 'voter-' + Math.random().toString(36).slice(2) + '-' + Date.now();

  localStorage.setItem(storage.voterId, generated);
  return generated;
}

function getDrafts(): Record<string, VoteState> {
  const raw = localStorage.getItem(storage.drafts);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, VoteState>;
    }
  } catch {
    return {};
  }
  return {};
}

function setDraft(cookieId: number, draft: VoteState): void {
  const all = getDrafts();
  all[String(cookieId)] = draft;
  localStorage.setItem(storage.drafts, JSON.stringify(all));
}

function getDraft(cookieId: number): VoteState | null {
  const all = getDrafts();
  return all[String(cookieId)] || null;
}

function defaultVoteState(): VoteState {
  return {
    overall: 0,
    appearance: 0,
    texture: 0,
    flavor: 0,
    superlatives: [],
    updated_at: ''
  };
}

function cookieGlyph(bitten: boolean): string {
  const base = '#dd8b42';
  const chip = '#4d2a1a';
  const bite = '#f7efe2';
  return `<svg class="cookie-glyph" viewBox="0 0 40 40" aria-hidden="true" role="img">
    <circle cx="20" cy="20" r="15" fill="${base}" />
    <circle cx="13" cy="14" r="2" fill="${chip}" />
    <circle cx="24" cy="13" r="1.8" fill="${chip}" />
    <circle cx="26" cy="22" r="2.1" fill="${chip}" />
    <circle cx="16" cy="24" r="1.7" fill="${chip}" />
    <circle cx="10" cy="22" r="1.5" fill="${chip}" />
    ${bitten ? `<circle cx="31" cy="10" r="6" fill="${bite}" /><circle cx="27" cy="8" r="2.2" fill="${bite}" /><circle cx="33" cy="14" r="2" fill="${bite}" />` : ''}
  </svg>`;
}

function renderCookieRatings(field: keyof Omit<VoteState, 'superlatives' | 'updated_at'>, current: number): string {
  const label = field.charAt(0).toUpperCase() + field.slice(1);
  let html = '<div class="field"><label>' + label + '</label><div class="cookie-rating">';
  for (let i = 1; i <= 5; i += 1) {
    const selected = current > 0 && i <= current;
    html += '<button type="button" class="cookie-rate-btn' + (selected ? ' active' : '') + '" data-value="' + i + '" data-field="' + field + '" aria-label="' + label + ' ' + i + ' out of 5">'
      + cookieGlyph(selected)
      + '</button>';
  }
  html += '</div></div>';
  return html;
}

function attachRatingHandlers(root: HTMLElement, voteState: VoteState): void {
  root.querySelectorAll<HTMLButtonElement>('.cookie-rate-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const value = Number(btn.dataset.value || '0');
      const field = btn.dataset.field || '';
      if (field !== 'overall' && field !== 'appearance' && field !== 'texture' && field !== 'flavor') {
        return;
      }

      voteState[field] = value;
      root.querySelectorAll<HTMLButtonElement>('.cookie-rate-btn[data-field="' + field + '"]').forEach((peer) => {
        const peerValue = Number(peer.dataset.value || '0');
        const isSelected = peerValue <= value;
        peer.classList.toggle('active', isSelected);
        peer.innerHTML = cookieGlyph(isSelected);
      });
    });
  });
}

function hasCompletedVote(cookieId: number): boolean {
  const draft = getDraft(cookieId);
  if (!draft) return false;
  return [draft.overall, draft.appearance, draft.texture, draft.flavor].every((score) => Number(score) > 0);
}

async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(file);
  });
}

async function normalizePhotoFile(file: File): Promise<string> {
  const originalDataUrl = await fileToDataUrl(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image preview.'));
    img.src = originalDataUrl;
  });

  const maxEdge = 900;
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not process image.');
  }

  ctx.drawImage(image, 0, 0, width, height);

  let quality = 0.84;
  let out = canvas.toDataURL('image/jpeg', quality);
  while (out.length > 1_400_000 && quality > 0.45) {
    quality -= 0.08;
    out = canvas.toDataURL('image/jpeg', quality);
  }

  if (out.length > 1_500_000) {
    throw new Error('Image is too large after compression. Please choose a smaller photo.');
  }

  return out;
}

function renderHome(): void {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  root.innerHTML = '<section class="hero fade-in">'
    + '<h1>Chocolate Chip Cookie Party</h1>'
    + '<p class="muted">Bring your best batch, submit your cookie, and crown the crowd favorite.</p>'
    + '<div style="margin-top:14px">' + navLinks() + '</div>'
    + '</section>'
    + '<section class="home-grid grid fade-in">'
    + '<article class="card stack">'
    + '<h2>1. Submit your cookie</h2>'
    + '<p class="muted">Add your cookie name, baker display name, and category details when you arrive.</p>'
    + '<a class="btn btn-primary cta" href="/submit">Go to submission</a>'
    + '</article>'
    + '<article class="card stack">'
    + '<h2>2. Vote and edit anytime</h2>'
    + '<p class="muted">Rate each cookie on overall, appearance, texture, and flavor. Add fun superlatives too.</p>'
    + '<a class="btn btn-primary cta" href="/vote">Go to voting</a>'
    + '</article>'
    + '<article class="card stack">'
    + '<h2>3. Print QR signs</h2>'
    + '<p class="muted">Open separate printable pages for submission and voting QR codes.</p>'
    + '<a class="btn btn-primary cta" href="/qr">Open QR pages</a>'
    + '</article>'
    + '</section>';

  wireNavMenu();
}

async function renderSubmit(): Promise<void> {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  root.innerHTML = '<section class="hero fade-in">'
    + '<h1>Cookie Submission</h1>'
    + '<p class="muted">Add each cookie when bakers arrive.</p>'
    + '<div style="margin-top:14px">' + navLinks() + '</div>'
    + '</section>'
    + '<section class="grid" style="margin-top:14px">'
    + '<article class="card stack">'
    + '<form id="submit-form" class="stack">'
    + '<div id="submit-message"></div>'
    + '<div class="field"><label for="cookie_name">Cookie name</label><input id="cookie_name" name="cookie_name" maxlength="90" required /></div>'
    + '<div class="field"><label for="baker_display_name">Baker display name</label><input id="baker_display_name" name="baker_display_name" maxlength="70" required /></div>'
    + '<div class="field"><label for="photo_file">Cookie photo</label><input id="photo_file" name="photo_file" type="file" accept="image/*" capture="environment" /><img id="photo_preview" class="cookie-photo cookie-photo--preview" alt="Cookie photo preview" style="display:none" /></div>'
    + '<div class="row">'
    + '<label class="chip"><input type="checkbox" id="gluten_free" /> Gluten free</label>'
    + '<label class="chip"><input type="checkbox" id="includes_extra_mixins" /> Includes mix-ins beyond chips</label>'
    + '<label class="chip"><input type="checkbox" id="dairy_free" /> Dairy free</label>'
    + '</div>'
    + '<div class="field"><label for="other_notes">Notes for voters</label><textarea id="other_notes" maxlength="220" placeholder="Flavor notes, ingredients, or story behind this cookie"></textarea></div>'
    + '<button class="btn btn-primary" type="submit">Add cookie and go vote</button>'
    + '</form>'
    + '</article>'
    + '<article class="card">'
    + '<h2>Current cookie lineup</h2>'
    + '<div id="cookie-list" class="cookie-list"><p class="muted">Loading cookies...</p></div>'
    + '</article>'
    + '</section>';

  wireNavMenu();

  const listNode = byId('cookie-list');
  const msgNode = byId('submit-message');
  const form = byId<HTMLFormElement>('submit-form');
  const photoInput = byId<HTMLInputElement>('photo_file');
  const photoPreview = byId<HTMLImageElement>('photo_preview');
  let photoDataUrl = '';

  photoInput?.addEventListener('change', async () => {
    const selected = photoInput.files?.[0];
    if (!selected) {
      photoDataUrl = '';
      if (photoPreview) {
        photoPreview.style.display = 'none';
      }
      return;
    }

    try {
      photoDataUrl = await normalizePhotoFile(selected);
      if (photoPreview) {
        photoPreview.src = photoDataUrl;
        photoPreview.style.display = 'block';
      }
    } catch (err) {
      photoDataUrl = '';
      if (photoPreview) {
        photoPreview.style.display = 'none';
      }
      if (msgNode) {
        msgNode.innerHTML = '<div class="error">' + escapeHtml((err as Error).message || String(err)) + '</div>';
      }
    }
  });

  const refreshCookies = async (): Promise<void> => {
    if (!listNode) return;
    try {
      const data = await api('/api/cookies');
      const cookies = data.cookies || [];
      if (!cookies.length) {
        listNode.innerHTML = '<p class="muted">No cookies yet. Be the first baker!</p>';
        return;
      }

      let html = '';
      (cookies as CookieRecord[]).forEach((cookie) => {
        const tags: string[] = [];
        if (cookie.gluten_free) tags.push('Gluten free');
        if (cookie.includes_extra_mixins) tags.push('Extra mix-ins');
        if (cookie.dairy_free) tags.push('Dairy free');
        const note = cookie.other_notes ? '<p class="muted"><strong>Notes:</strong> ' + escapeHtml(cookie.other_notes) + '</p>' : '';
        const photo = cookie.photo_data_url
          ? '<img class="cookie-photo cookie-photo--thumb" src="' + escapeHtml(cookie.photo_data_url) + '" alt="Photo of ' + escapeHtml(cookie.cookie_name) + '" />'
          : '';

        html += '<div class="cookie-item">'
          + photo
          + '<strong>' + escapeHtml(cookie.cookie_name) + '</strong>'
          + '<span class="muted">By ' + escapeHtml(cookie.baker_display_name) + '</span>'
          + '<div class="row">' + tags.map((tag) => '<span class="chip">' + escapeHtml(tag) + '</span>').join('') + '</div>'
          + note
          + '</div>';
      });

      listNode.innerHTML = html;
    } catch (err) {
      listNode.innerHTML = '<div class="error">Failed to load cookies: ' + escapeHtml((err as Error).message || String(err)) + '</div>';
    }
  };

  await refreshCookies();

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();

    const payload = {
      cookie_name: String(byId<HTMLInputElement>('cookie_name')?.value || '').trim(),
      baker_display_name: String(byId<HTMLInputElement>('baker_display_name')?.value || '').trim(),
      gluten_free: Boolean(byId<HTMLInputElement>('gluten_free')?.checked),
      includes_extra_mixins: Boolean(byId<HTMLInputElement>('includes_extra_mixins')?.checked),
      dairy_free: Boolean(byId<HTMLInputElement>('dairy_free')?.checked),
      other_notes: String(byId<HTMLTextAreaElement>('other_notes')?.value || '').trim()
      ,
      photo_data_url: photoDataUrl
    };

    try {
      await api('/api/cookies', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (msgNode) {
        msgNode.innerHTML = '<div class="notice">Cookie added. Heading to the voting page...</div>';
      }
      setTimeout(() => {
        location.href = '/vote';
      }, 300);
    } catch (err) {
      if (msgNode) {
        msgNode.innerHTML = '<div class="error">' + escapeHtml((err as Error).message || String(err)) + '</div>';
      }
    }
  });
}

async function renderVote(): Promise<void> {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  const voterId = getVoterId();
  root.innerHTML = '<section class="hero fade-in">'
    + '<h1>Cookie Voting</h1>'
    + '<p class="muted">Tap one cookie, rate it, save, and return to the list.</p>'
    + '<div style="margin-top:14px">' + navLinks() + '</div>'
    + '</section>'
    + '<section class="card vote-flow">'
    + (showVoterId ? '<div class="muted">Voter ID: ' + escapeHtml(voterId) + '</div>' : '')
    + '<div id="vote-message" style="margin-top:10px"></div>'
    + '<div id="vote-stage" style="margin-top:12px"></div>'
    + '</section>';

  wireNavMenu();

  const messageNode = byId('vote-message');
  const stage = byId('vote-stage');
  if (!stage) return;

  let cookies: CookieRecord[] = [];
  let selectedCookie: CookieRecord | null = null;
  let search = '';

  const setMessage = (type: 'notice' | 'error', text: string): void => {
    if (!messageNode) return;
    const className = type === 'error' ? 'error' : 'notice';
    messageNode.innerHTML = '<div class="' + className + '">' + escapeHtml(text) + '</div>';
  };

  const mergeVotes = (localVote: VoteState | null, serverVote: VoteState | null): VoteState => {
    if (!localVote && !serverVote) return defaultVoteState();
    if (!localVote) return serverVote as VoteState;
    if (!serverVote) return localVote;

    const localTime = Date.parse(localVote.updated_at || '') || 0;
    const serverTime = Date.parse(serverVote.updated_at || '') || 0;
    return localTime >= serverTime ? localVote : serverVote;
  };

  const loadVoteForCookie = async (cookieId: number): Promise<VoteState> => {
    const localVote = getDraft(cookieId);
    let serverVote: VoteState | null = null;
    try {
      const data = await api('/api/votes/' + cookieId + '?voterId=' + encodeURIComponent(voterId));
      serverVote = data.vote || null;
    } catch {
      serverVote = null;
    }
    return mergeVotes(localVote, serverVote);
  };

  const renderCookiePicker = (): void => {
    if (!cookies.length) {
      stage.innerHTML = '<p class="muted">No cookies are submitted yet. Check back soon.</p>';
      return;
    }

    const q = search.trim().toLowerCase();
    const filtered = cookies.filter((cookie) => {
      return [cookie.cookie_name, cookie.baker_display_name, cookie.other_notes]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });

    let html = '<div class="stack">'
      + '<h2>Select a cookie to rate</h2>'
      + '<div class="field"><label for="cookie-search">Search cookies</label><input id="cookie-search" type="text" placeholder="Search by cookie, baker, or notes" value="' + escapeHtml(search) + '" /></div>'
      + '<p class="muted">Showing ' + filtered.length + ' of ' + cookies.length + ' cookies</p>'
      + '<div class="cookie-list">';

    filtered.forEach((cookie) => {
      const notes = cookie.other_notes ? '<span class="muted"><strong>Notes:</strong> ' + escapeHtml(cookie.other_notes) + '</span>' : '';
      const voted = hasCompletedVote(cookie.id);
      const badge = '<span class="vote-badge ' + (voted ? 'is-voted' : 'is-unvoted') + '">' + (voted ? 'Already voted' : 'Not voted yet') + '</span>';
      const photo = cookie.photo_data_url
        ? '<img class="cookie-photo cookie-photo--thumb" src="' + escapeHtml(cookie.photo_data_url) + '" alt="Photo of ' + escapeHtml(cookie.cookie_name) + '" />'
        : '';
      html += '<button class="cookie-item" data-cookie-id="' + cookie.id + '" type="button">'
        + photo
        + '<strong>' + escapeHtml(cookie.cookie_name) + '</strong>'
        + '<span class="muted">By ' + escapeHtml(cookie.baker_display_name) + '</span>'
        + badge
        + notes
        + '<span class="muted">Votes so far: ' + Number(cookie.vote_count || 0) + '</span>'
        + '</button>';
    });

    if (!filtered.length) {
      html += '<div class="panel"><p class="muted">No cookies match this search.</p></div>';
    }

    html += '</div></div>';

    stage.innerHTML = html;

    const searchInput = byId<HTMLInputElement>('cookie-search');
    searchInput?.addEventListener('input', () => {
      const cursor = searchInput.selectionStart ?? searchInput.value.length;
      search = searchInput.value;
      renderCookiePicker();
      requestAnimationFrame(() => {
        const next = byId<HTMLInputElement>('cookie-search');
        if (!next) return;
        next.focus();
        next.setSelectionRange(cursor, cursor);
      });
    });

    stage.querySelectorAll<HTMLButtonElement>('[data-cookie-id]').forEach((node) => {
      node.addEventListener('click', async () => {
        const id = Number(node.dataset.cookieId || '0');
        selectedCookie = cookies.find((cookie) => cookie.id === id) || null;
        await renderVoteForm();
      });
    });
  };

  const renderVoteForm = async (): Promise<void> => {
    if (!selectedCookie) {
      renderCookiePicker();
      return;
    }

    const voteState = await loadVoteForCookie(selectedCookie.id);
    const notes = selectedCookie.other_notes
      ? '<div class="panel"><strong>Baker notes</strong><p class="muted">' + escapeHtml(selectedCookie.other_notes) + '</p></div>'
      : '';
    const photo = selectedCookie.photo_data_url
      ? '<img class="cookie-photo cookie-photo--large" src="' + escapeHtml(selectedCookie.photo_data_url) + '" alt="Photo of ' + escapeHtml(selectedCookie.cookie_name) + '" />'
      : '';

    let html = '<div class="stack fade-in">'
      + '<div class="row">'
      + '<button type="button" class="btn btn-ghost" id="back-to-list">Back to cookie list</button>'
      + '<button type="button" class="btn btn-primary" id="save-vote-top">Save vote</button>'
      + '</div>'
      + '<h2>' + escapeHtml(selectedCookie.cookie_name) + '</h2>'
      + '<p class="muted">By ' + escapeHtml(selectedCookie.baker_display_name) + '</p>'
      + photo
      + notes
      + renderCookieRatings('overall', Number(voteState.overall || 0))
      + renderCookieRatings('appearance', Number(voteState.appearance || 0))
      + renderCookieRatings('texture', Number(voteState.texture || 0))
      + renderCookieRatings('flavor', Number(voteState.flavor || 0))
      + '<div class="field"><label>Superlatives</label><div class="stack">';

    SUPERLATIVES.forEach((entry) => {
      const checked = (voteState.superlatives || []).includes(entry.key) ? 'checked' : '';
      html += '<label class="chip"><input type="checkbox" value="' + entry.key + '" class="superlative" ' + checked + ' /> ' + escapeHtml(entry.label) + '</label>';
    });

    html += '</div></div>'
      + '<div class="sticky-actions">'
      + '<span class="muted">Last saved: ' + (voteState.updated_at ? escapeHtml(new Date(voteState.updated_at).toLocaleString()) : 'Not saved yet') + '</span>'
      + '<button type="button" class="btn btn-primary" id="save-vote">Save vote</button>'
      + '</div>'
      + '</div>';

    stage.innerHTML = html;
    attachRatingHandlers(stage, voteState);

    const back = byId<HTMLButtonElement>('back-to-list');
    const save = byId<HTMLButtonElement>('save-vote');
    const saveTop = byId<HTMLButtonElement>('save-vote-top');

    back?.addEventListener('click', () => {
      selectedCookie = null;
      renderCookiePicker();
    });

    const saveVote = async (): Promise<void> => {
      const selectedSupers: string[] = [];
      stage.querySelectorAll<HTMLInputElement>('.superlative:checked').forEach((node) => {
        selectedSupers.push(String(node.value));
      });

      voteState.superlatives = selectedSupers;
      const required: Array<keyof VoteState> = ['overall', 'appearance', 'texture', 'flavor'];
      for (const key of required) {
        if (!Number(voteState[key])) {
          setMessage('error', 'Please pick a 1-5 score for ' + key + '.');
          return;
        }
      }

      const payload = {
        voterId,
        ratings: {
          overall: Number(voteState.overall),
          appearance: Number(voteState.appearance),
          texture: Number(voteState.texture),
          flavor: Number(voteState.flavor)
        },
        superlatives: selectedSupers
      };

      try {
        const result = await api('/api/votes/' + selectedCookie?.id, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const updated = result.vote || payload.ratings;
        voteState.updated_at = updated.updated_at || new Date().toISOString();
        setDraft(selectedCookie!.id, voteState);
        setMessage('notice', 'Vote saved for ' + selectedCookie?.cookie_name + '.');

        const refreshed = await api('/api/cookies');
        cookies = refreshed.cookies || cookies;
        selectedCookie = null;
        renderCookiePicker();
      } catch (err) {
        setMessage('error', (err as Error).message || String(err));
      }
    };

    save?.addEventListener('click', saveVote);
    saveTop?.addEventListener('click', saveVote);
  };

  try {
    const data = await api('/api/cookies');
    cookies = data.cookies || [];
    renderCookiePicker();
  } catch (err) {
    stage.innerHTML = '<div class="error">Failed to load cookies: ' + escapeHtml((err as Error).message || String(err)) + '</div>';
  }
}

function winnerText(entries: WinnerEntry[], metricLabel: string, valueLabel: 'score' | 'vote_count'): string {
  if (!entries.length) {
    return '<p class="muted">No winner yet.</p>';
  }

  const tie = entries.length > 1;
  const suffix = tie ? ' (tie)' : '';
  let html = '<strong>' + escapeHtml(metricLabel) + suffix + '</strong><div class="stack">';
  entries.forEach((entry) => {
    const value = valueLabel === 'score'
      ? Number(entry.score ?? 0).toFixed(2) + ' / 5'
      : Number(entry.vote_count ?? 0) + ' votes';
    const photo = entry.photo_data_url
      ? '<img class="cookie-photo cookie-photo--winner" src="' + escapeHtml(entry.photo_data_url) + '" alt="Photo of ' + escapeHtml(entry.cookie_name) + '" />'
      : '';
    html += '<div class="winner-inline">' + photo + '<div><div><strong>' + escapeHtml(entry.cookie_name) + '</strong> <span class="muted">by ' + escapeHtml(entry.baker_display_name) + '</span></div><div class="muted">' + value + '</div></div></div>';
  });
  html += '</div>';
  return html;
}

function pickSingleWinner(
  rows: ResultsPayload['results'],
  key: 'overall_avg' | 'appearance_avg' | 'texture_avg' | 'flavor_avg'
): WinnerEntry | null {
  const valid = rows.filter((row) => row.vote_count > 0);
  if (!valid.length) {
    return null;
  }

  const sorted = [...valid].sort((a, b) => {
    const diff = Number(b[key] ?? 0) - Number(a[key] ?? 0);
    if (diff !== 0) return diff;
    if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count;
    return a.cookie_name.localeCompare(b.cookie_name);
  });

  const top = sorted[0];
  return {
    cookie_name: top.cookie_name,
    baker_display_name: top.baker_display_name,
    photo_data_url: top.photo_data_url,
    score: Number(top[key] ?? 0),
    vote_count: top.vote_count
  };
}

async function renderResults(): Promise<void> {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  const main = document.querySelector('main');
  if (main) {
    main.classList.add('tv-mode');
  }

  root.innerHTML = '<section class="card">'
    + '<div id="results-lock" class="stack">'
    + '<div class="field"><label for="passphrase">Passphrase</label><input id="passphrase" type="password" autocomplete="current-password" placeholder="Enter passphrase" /></div>'
    + '<button type="button" class="btn btn-primary" id="unlock-results">Unlock</button>'
    + '<div id="results-error"></div>'
    + '</div>'
    + '<div id="results-data" style="margin-top:14px"></div>'
    + '</section>';

  const passphraseInput = byId<HTMLInputElement>('passphrase');
  const lockNode = byId('results-lock');
  const unlockBtn = byId<HTMLButtonElement>('unlock-results');
  const resultsError = byId('results-error');
  const resultsData = byId('results-data');
  let refreshTimer: number | null = null;
  let lastPaintKey = '';

  const prior = sessionStorage.getItem(storage.resultsPassphrase);
  if (prior && passphraseInput) {
    passphraseInput.value = prior;
  }

  const setError = (text: string): void => {
    if (!resultsError) return;
    resultsError.innerHTML = text ? '<div class="error">' + escapeHtml(text) + '</div>' : '';
  };

  const paintResults = (data: ResultsPayload): void => {
    if (!resultsData) return;

    const nextPaintKey = JSON.stringify({
      results: data.results,
      category_winners: data.category_winners,
      superlative_winners: data.superlative_winners
    });
    if (nextPaintKey === lastPaintKey) {
      return;
    }
    lastPaintKey = nextPaintKey;

    const rows = data.results || [];
    const categoryWinners = data.category_winners || { overall: [], appearance: [], texture: [], flavor: [] };
    const superlativeWinners = data.superlative_winners || {};

    if (!rows.length) {
      resultsData.innerHTML = '<p class="muted">No votes yet.</p>';
      return;
    }

    const overallWinners = categoryWinners.overall || [];
    const appearanceWinner = pickSingleWinner(rows, 'appearance_avg');
    const textureWinner = pickSingleWinner(rows, 'texture_avg');
    const flavorWinner = pickSingleWinner(rows, 'flavor_avg');

    let tableRows = '';
    let previousOverall = Number.NaN;
    let previousTieBreaker = Number.NaN;
    let previousRank = 0;

    rows.forEach((row, index) => {
      const overall = Number(row.overall_avg || 0);
      const tieBreaker = Number(row.appearance_avg || 0) + Number(row.texture_avg || 0) + Number(row.flavor_avg || 0);
      const sameAsPrevious = index > 0
        && Math.abs(overall - previousOverall) < 0.000001
        && Math.abs(tieBreaker - previousTieBreaker) < 0.000001;
      const displayRank = sameAsPrevious ? previousRank : index + 1;

      previousOverall = overall;
      previousTieBreaker = tieBreaker;
      previousRank = displayRank;

      const notes = row.other_notes ? '<div class="muted">Notes: ' + escapeHtml(row.other_notes) + '</div>' : '';
      const photo = row.photo_data_url
        ? '<img class="cookie-photo cookie-photo--thumb" src="' + escapeHtml(row.photo_data_url) + '" alt="Photo of ' + escapeHtml(row.cookie_name) + '" />'
        : '';
      tableRows += '<tr>'
        + '<td>#' + displayRank + '</td>'
        + '<td>' + photo + '<strong>' + escapeHtml(row.cookie_name) + '</strong><div class="muted">by ' + escapeHtml(row.baker_display_name) + '</div>' + notes + '</td>'
        + '<td>' + Number(row.overall_avg || 0).toFixed(2) + '</td>'
        + '<td>' + Number(row.appearance_avg || 0).toFixed(2) + '</td>'
        + '<td>' + Number(row.texture_avg || 0).toFixed(2) + '</td>'
        + '<td>' + Number(row.flavor_avg || 0).toFixed(2) + '</td>'
        + '<td>' + Number(row.vote_count || 0) + '</td>'
        + '</tr>';
    });

    const superCards = SUPERLATIVES.map((entry) => {
      const winners = superlativeWinners[entry.key] || [];
      return '<div class="panel stack">' + winnerText(winners, entry.label, 'vote_count') + '</div>';
    }).join('');

    const winnerCard = (label: string, winner: WinnerEntry | null): string => {
      if (!winner) {
        return '<div class="panel stack"><strong>' + escapeHtml(label) + '</strong><p class="muted">No winner yet.</p></div>';
      }
      return '<div class="panel stack">'
        + '<strong>' + escapeHtml(label) + '</strong>'
        + '<div><strong>' + escapeHtml(winner.cookie_name) + '</strong> <span class="muted">by ' + escapeHtml(winner.baker_display_name) + '</span></div>'
        + '<div class="muted">' + Number(winner.score ?? 0).toFixed(2) + ' / 5</div>'
        + '</div>';
    };

    const overallWinnerHtml = overallWinners.length
      ? '<div class="winner-main"><div class="trophy" aria-hidden="true">🏆</div><div>'
        + '<h2>' + (overallWinners.length > 1 ? 'Overall Winners (Tie)' : 'Overall Winner') + '</h2>'
        + overallWinners.map((winner) => '<div class="stack" style="align-items:center">'
            + (winner.photo_data_url ? '<img class="cookie-photo cookie-photo--winner-main" src="' + escapeHtml(winner.photo_data_url) + '" alt="Photo of ' + escapeHtml(winner.cookie_name) + '" />' : '')
            + '<p><strong>' + escapeHtml(winner.cookie_name) + '</strong> <span class="muted">by ' + escapeHtml(winner.baker_display_name) + '</span></p>'
            + '<p class="muted">Score: ' + Number(winner.score ?? 0).toFixed(2) + ' / 5</p>'
          + '</div>').join('')
        + '</div></div>'
      : '<p class="muted">No winner yet.</p>';

    resultsData.innerHTML = '<div class="podium stack">'
      + overallWinnerHtml
      + '</div>'
      + '<div class="results-grid">'
      + winnerCard('Appearance winner', appearanceWinner)
      + winnerCard('Texture winner', textureWinner)
      + winnerCard('Flavor winner', flavorWinner)
      + '</div>'
      + '<div class="card" style="margin-top:12px">'
      + '<h2>Superlative winners</h2>'
      + '<div class="results-grid">' + superCards + '</div>'
      + '</div>'
      + '<div class="card" style="margin-top:12px">'
      + '<h2>Leaderboard</h2>'
      + '<div style="overflow:auto"><table class="table"><thead><tr><th>Rank</th><th>Cookie</th><th>Overall</th><th>Appearance</th><th>Texture</th><th>Flavor</th><th>Votes</th></tr></thead><tbody>'
      + tableRows
      + '</tbody></table></div>'
      + '</div>';
  };

  const loadResults = async (): Promise<boolean> => {
    const passphrase = String(passphraseInput?.value || '').trim();
    if (!passphrase) {
      setError('Enter passphrase first.');
      return false;
    }
      try {
      const data = await api('/api/results', {
        headers: {
          'x-results-passphrase': passphrase
        }
      });
      sessionStorage.setItem(storage.resultsPassphrase, passphrase);
      paintResults(data as ResultsPayload);
      setError('');
      if (lockNode) {
        lockNode.style.display = 'none';
      }
      return true;
    } catch (err) {
      setError((err as Error).message || String(err));
      return false;
    }
  };

  unlockBtn?.addEventListener('click', async () => {
    const ok = await loadResults();
    if (!ok) return;
    if (refreshTimer) window.clearInterval(refreshTimer);
    refreshTimer = window.setInterval(async () => {
      await loadResults();
    }, 15000);
  });

  if (prior) {
    const ok = await loadResults();
    if (ok) {
      refreshTimer = window.setInterval(async () => {
        await loadResults();
      }, 15000);
    }
  }
}

async function renderAdmin(): Promise<void> {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  root.innerHTML = '<section class="hero fade-in">'
    + '<h1>Admin Console</h1>'
    + '<p class="muted">Manage cookies, votes, and party controls.</p>'
    + '<div style="margin-top:14px">' + navLinks() + '</div>'
    + '</section>'
    + '<section class="card" style="margin-top:14px">'
    + '<div id="admin-lock" class="stack">'
    + '<div class="field"><label for="admin-passphrase">Passphrase</label><input id="admin-passphrase" type="password" autocomplete="current-password" placeholder="Enter passphrase" /></div>'
    + '<button type="button" class="btn btn-primary" id="admin-unlock">Unlock admin</button>'
    + '<div id="admin-error"></div>'
    + '</div>'
    + '<div id="admin-content" style="display:none"></div>'
    + '</section>';

  wireNavMenu();

  const lockNode = byId('admin-lock');
  const contentNode = byId('admin-content');
  const passInput = byId<HTMLInputElement>('admin-passphrase');
  const unlockBtn = byId<HTMLButtonElement>('admin-unlock');
  const errorNode = byId('admin-error');

  let passphrase = sessionStorage.getItem(storage.resultsPassphrase) || '';
  if (passInput && passphrase) {
    passInput.value = passphrase;
  }

  const setError = (message: string): void => {
    if (!errorNode) return;
    errorNode.innerHTML = message ? '<div class="error">' + escapeHtml(message) + '</div>' : '';
  };

  const adminApi = async (path: string, options?: RequestInit): Promise<any> => {
    const headers = new Headers(options?.headers || {});
    headers.set('x-results-passphrase', passphrase);
    return await api(path, {
      ...options,
      headers
    });
  };

  const renderDashboard = (overview: AdminOverviewPayload): void => {
    if (!contentNode) return;

    let cookieRows = '';
    overview.cookies.forEach((cookie) => {
      const photo = cookie.photo_data_url
        ? '<img class="cookie-photo cookie-photo--thumb" src="' + escapeHtml(cookie.photo_data_url) + '" alt="Photo of ' + escapeHtml(cookie.cookie_name) + '" />'
        : '';

      cookieRows += '<tr>'
        + '<td>' + photo + '<strong>' + escapeHtml(cookie.cookie_name) + '</strong><div class="muted">by ' + escapeHtml(cookie.baker_display_name) + '</div></td>'
        + '<td>'
        + '<button class="btn btn-ghost admin-edit-cookie" data-cookie-id="' + cookie.id + '" type="button">Edit</button> '
        + '<button class="btn btn-ghost admin-delete-cookie-votes" data-cookie-id="' + cookie.id + '" type="button">Delete cookie votes</button> '
        + '<button class="btn btn-ghost admin-delete-cookie" data-cookie-id="' + cookie.id + '" type="button">Delete cookie</button>'
        + '</td>'
        + '</tr>';
    });

    let voteRows = '';
    overview.votes.forEach((vote) => {
      voteRows += '<tr>'
        + '<td>' + vote.id + '</td>'
        + '<td>' + escapeHtml(vote.voter_id) + '</td>'
        + '<td><strong>' + escapeHtml(vote.cookie_name) + '</strong><div class="muted">by ' + escapeHtml(vote.baker_display_name) + '</div></td>'
        + '<td>' + vote.overall + '/' + vote.appearance + '/' + vote.texture + '/' + vote.flavor + '</td>'
        + '<td><button class="btn btn-ghost admin-delete-vote" data-vote-id="' + vote.id + '" type="button">Delete vote</button></td>'
        + '</tr>';
    });

    contentNode.innerHTML = '<div class="stack">'
      + '<div class="panel stack">'
      + '<h2>Party controls</h2>'
      + '<label class="chip"><input type="checkbox" id="admin-allow-submissions" ' + (overview.settings.allow_submissions ? 'checked' : '') + ' /> Allow new cookie submissions</label>'
      + '<label class="chip"><input type="checkbox" id="admin-allow-voting" ' + (overview.settings.allow_voting ? 'checked' : '') + ' /> Allow voting</label>'
      + '<div class="row">'
      + '<button class="btn btn-primary" id="admin-save-settings" type="button">Save controls</button>'
      + '<button class="btn btn-ghost" id="admin-delete-all-votes" type="button">Delete all votes</button>'
      + '<button class="btn btn-ghost" id="admin-reset-all" type="button">Delete EVERYTHING</button>'
      + '</div>'
      + '</div>'
      + '<div class="panel stack">'
      + '<h2>Delete votes by voter id</h2>'
      + '<div class="row">'
      + '<input id="admin-voter-id" type="text" placeholder="Voter ID" />'
      + '<button class="btn btn-ghost" id="admin-delete-voter-votes" type="button">Delete voter votes</button>'
      + '</div>'
      + '</div>'
      + '<div class="panel stack">'
      + '<h2>Edit cookie</h2>'
      + '<div class="field"><label for="admin-edit-cookie-id">Cookie</label><select id="admin-edit-cookie-id">'
      + overview.cookies.map((cookie) => '<option value="' + cookie.id + '">' + escapeHtml(cookie.cookie_name) + ' (by ' + escapeHtml(cookie.baker_display_name) + ')</option>').join('')
      + '</select></div>'
      + '<div class="field"><label for="admin-edit-cookie-name">Cookie name</label><input id="admin-edit-cookie-name" type="text" /></div>'
      + '<div class="field"><label for="admin-edit-baker">Baker display name</label><input id="admin-edit-baker" type="text" /></div>'
      + '<div class="field"><label for="admin-edit-photo">Photo</label><input id="admin-edit-photo" type="file" accept="image/*" capture="environment" /><img id="admin-edit-photo-preview" class="cookie-photo cookie-photo--preview" style="display:none" alt="Cookie photo preview" /></div>'
      + '<label class="chip"><input type="checkbox" id="admin-remove-photo" /> Remove photo</label>'
      + '<div class="row">'
      + '<label class="chip"><input type="checkbox" id="admin-edit-gluten" /> Gluten free</label>'
      + '<label class="chip"><input type="checkbox" id="admin-edit-mixins" /> Extra mix-ins</label>'
      + '<label class="chip"><input type="checkbox" id="admin-edit-dairy" /> Dairy free</label>'
      + '</div>'
      + '<div class="field"><label for="admin-edit-notes">Notes</label><textarea id="admin-edit-notes"></textarea></div>'
      + '<button class="btn btn-primary" id="admin-save-cookie" type="button">Save cookie changes</button>'
      + '</div>'
      + '<div class="panel stack">'
      + '<h2>Cookies</h2>'
      + '<div style="overflow:auto"><table class="table"><thead><tr><th>Cookie</th><th>Actions</th></tr></thead><tbody>' + cookieRows + '</tbody></table></div>'
      + '</div>'
      + '<div class="panel stack">'
      + '<h2>Votes</h2>'
      + '<div style="overflow:auto"><table class="table"><thead><tr><th>ID</th><th>Voter</th><th>Cookie</th><th>Scores</th><th>Action</th></tr></thead><tbody>' + voteRows + '</tbody></table></div>'
      + '</div>'
      + '</div>';

    let editPhotoDataUrl = '';

    const getCookieById = (id: number): CookieRecord | undefined => overview.cookies.find((cookie) => cookie.id === id);

    const loadCookieIntoForm = (id: number): void => {
      const cookie = getCookieById(id);
      if (!cookie) return;

      editPhotoDataUrl = cookie.photo_data_url || '';
      const name = byId<HTMLInputElement>('admin-edit-cookie-name');
      const baker = byId<HTMLInputElement>('admin-edit-baker');
      const notes = byId<HTMLTextAreaElement>('admin-edit-notes');
      const gluten = byId<HTMLInputElement>('admin-edit-gluten');
      const mixins = byId<HTMLInputElement>('admin-edit-mixins');
      const dairy = byId<HTMLInputElement>('admin-edit-dairy');
      const preview = byId<HTMLImageElement>('admin-edit-photo-preview');
      const removePhoto = byId<HTMLInputElement>('admin-remove-photo');

      if (name) name.value = cookie.cookie_name;
      if (baker) baker.value = cookie.baker_display_name;
      if (notes) notes.value = cookie.other_notes || '';
      if (gluten) gluten.checked = Boolean(cookie.gluten_free);
      if (mixins) mixins.checked = Boolean(cookie.includes_extra_mixins);
      if (dairy) dairy.checked = Boolean(cookie.dairy_free);
      if (removePhoto) removePhoto.checked = false;

      if (preview) {
        if (cookie.photo_data_url) {
          preview.src = cookie.photo_data_url;
          preview.style.display = 'block';
        } else {
          preview.style.display = 'none';
        }
      }
    };

    const select = byId('admin-edit-cookie-id') as HTMLSelectElement | null;
    if (select && overview.cookies.length) {
      loadCookieIntoForm(Number(select.value));
      select.addEventListener('change', () => {
        loadCookieIntoForm(Number(select.value));
      });
    }

    const editPhotoInput = byId<HTMLInputElement>('admin-edit-photo');
    editPhotoInput?.addEventListener('change', async () => {
      const file = editPhotoInput.files?.[0];
      if (!file) return;
      try {
        editPhotoDataUrl = await normalizePhotoFile(file);
        const preview = byId<HTMLImageElement>('admin-edit-photo-preview');
        if (preview) {
          preview.src = editPhotoDataUrl;
          preview.style.display = 'block';
        }
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    byId<HTMLButtonElement>('admin-save-settings')?.addEventListener('click', async () => {
      try {
        await adminApi('/api/admin/settings', {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            allow_submissions: Boolean(byId<HTMLInputElement>('admin-allow-submissions')?.checked),
            allow_voting: Boolean(byId<HTMLInputElement>('admin-allow-voting')?.checked)
          })
        });
        await refreshOverview();
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    byId<HTMLButtonElement>('admin-delete-all-votes')?.addEventListener('click', async () => {
      if (!confirm('Delete ALL votes?')) return;
      try {
        await adminApi('/api/admin/delete-all-votes', { method: 'POST' });
        await refreshOverview();
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    byId<HTMLButtonElement>('admin-reset-all')?.addEventListener('click', async () => {
      if (!confirm('Delete EVERYTHING in the database?')) return;
      try {
        await adminApi('/api/admin/reset-all', { method: 'POST' });
        await refreshOverview();
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    byId<HTMLButtonElement>('admin-delete-voter-votes')?.addEventListener('click', async () => {
      const voterId = String(byId<HTMLInputElement>('admin-voter-id')?.value || '').trim();
      if (!voterId) {
        setError('Provide a voter id first.');
        return;
      }
      if (!confirm('Delete all votes for voter id: ' + voterId + '?')) return;
      try {
        await adminApi('/api/admin/votes/delete-by-voter', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ voter_id: voterId })
        });
        await refreshOverview();
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    byId<HTMLButtonElement>('admin-save-cookie')?.addEventListener('click', async () => {
      const editCookieSelect = byId('admin-edit-cookie-id') as HTMLSelectElement | null;
      const cookieId = Number(editCookieSelect?.value || '0');
      if (!cookieId) {
        setError('Select a cookie to edit.');
        return;
      }

      const removePhoto = Boolean(byId<HTMLInputElement>('admin-remove-photo')?.checked);
      const payload = {
        cookie_name: String(byId<HTMLInputElement>('admin-edit-cookie-name')?.value || '').trim(),
        baker_display_name: String(byId<HTMLInputElement>('admin-edit-baker')?.value || '').trim(),
        photo_data_url: removePhoto ? '' : editPhotoDataUrl,
        gluten_free: Boolean(byId<HTMLInputElement>('admin-edit-gluten')?.checked),
        includes_extra_mixins: Boolean(byId<HTMLInputElement>('admin-edit-mixins')?.checked),
        dairy_free: Boolean(byId<HTMLInputElement>('admin-edit-dairy')?.checked),
        other_notes: String(byId<HTMLTextAreaElement>('admin-edit-notes')?.value || '').trim()
      };

      try {
        await adminApi('/api/admin/cookies/' + cookieId, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload)
        });
        await refreshOverview();
      } catch (err) {
        setError((err as Error).message || String(err));
      }
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-delete-cookie').forEach((button) => {
      button.addEventListener('click', async () => {
        const cookieId = Number(button.dataset.cookieId || '0');
        if (!cookieId) return;
        if (!confirm('Delete this cookie and all its votes?')) return;
        try {
          await adminApi('/api/admin/cookies/' + cookieId, { method: 'DELETE' });
          await refreshOverview();
        } catch (err) {
          setError((err as Error).message || String(err));
        }
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-delete-cookie-votes').forEach((button) => {
      button.addEventListener('click', async () => {
        const cookieId = Number(button.dataset.cookieId || '0');
        if (!cookieId) return;
        if (!confirm('Delete all votes for this cookie?')) return;
        try {
          await adminApi('/api/admin/cookies/' + cookieId + '/delete-votes', { method: 'POST' });
          await refreshOverview();
        } catch (err) {
          setError((err as Error).message || String(err));
        }
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-edit-cookie').forEach((button) => {
      button.addEventListener('click', () => {
        const cookieId = Number(button.dataset.cookieId || '0');
        const selectEl = byId('admin-edit-cookie-id') as HTMLSelectElement | null;
        if (!cookieId || !selectEl) return;
        selectEl.value = String(cookieId);
        loadCookieIntoForm(cookieId);
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-delete-vote').forEach((button) => {
      button.addEventListener('click', async () => {
        const voteId = Number(button.dataset.voteId || '0');
        if (!voteId) return;
        if (!confirm('Delete vote #' + voteId + '?')) return;
        try {
          await adminApi('/api/admin/votes/' + voteId, { method: 'DELETE' });
          await refreshOverview();
        } catch (err) {
          setError((err as Error).message || String(err));
        }
      });
    });
  };

  const refreshOverview = async (): Promise<void> => {
    const overview = (await adminApi('/api/admin/overview')) as AdminOverviewPayload;
    if (contentNode) {
      contentNode.style.display = 'block';
    }
    if (lockNode) {
      lockNode.style.display = 'none';
    }
    setError('');
    renderDashboard(overview);
  };

  const unlock = async (): Promise<void> => {
    passphrase = String(passInput?.value || '').trim();
    if (!passphrase) {
      setError('Enter passphrase first.');
      return;
    }
    sessionStorage.setItem(storage.resultsPassphrase, passphrase);
    try {
      await refreshOverview();
    } catch (err) {
      setError((err as Error).message || String(err));
    }
  };

  unlockBtn?.addEventListener('click', async () => {
    await unlock();
  });

  if (passphrase) {
    try {
      await refreshOverview();
    } catch {
      setError('Stored passphrase failed. Re-enter passphrase.');
    }
  }
}

function qrImage(url: string): string {
  return 'https://api.qrserver.com/v1/create-qr-code/?size=420x420&data=' + encodeURIComponent(url);
}

function renderQrIndex(): void {
  setPageMode('default');
  const root = appRoot();
  if (!root) return;

  root.innerHTML = '<section class="hero fade-in">'
    + '<h1>QR Print Pages</h1>'
    + '<p class="muted">Use separate pages so each sign can be printed on its own paper.</p>'
    + '<div style="margin-top:14px">' + navLinks() + '</div>'
    + '</section>'
    + '<section class="home-grid grid fade-in">'
    + '<article class="card stack">'
    + '<h2>Submission QR</h2>'
    + '<p class="muted">Single-page sign for cookie submissions.</p>'
    + '<a class="btn btn-primary cta" href="/qr/submit">Open submission QR</a>'
    + '</article>'
    + '<article class="card stack">'
    + '<h2>Voting QR</h2>'
    + '<p class="muted">Single-page sign for cookie voting.</p>'
    + '<a class="btn btn-primary cta" href="/qr/vote">Open voting QR</a>'
    + '</article>'
    + '</section>';

  wireNavMenu();
}

function renderSingleQrPage(type: 'submit' | 'vote'): void {
  setPageMode('print-qr');
  const root = appRoot();
  if (!root) return;

  const url = type === 'submit' ? location.origin + '/submit' : location.origin + '/vote';
  const title = type === 'submit' ? 'Submit Cookie' : 'Vote on Cookies';

  root.innerHTML = '<section class="qr-clean fade-in">'
    + '<h1>' + escapeHtml(title) + '</h1>'
    + '<img class="qr" src="' + qrImage(url) + '" alt="QR code" />'
    + '<p>' + escapeHtml(url) + '</p>'
    + '</section>';
}

async function start(): Promise<void> {
  const current = route();
  if (current === ROUTES.submit) {
    await renderSubmit();
    return;
  }
  if (current === ROUTES.vote) {
    await renderVote();
    return;
  }
  if (current === ROUTES.results) {
    await renderResults();
    return;
  }
  if (current === ROUTES.admin) {
    await renderAdmin();
    return;
  }
  if (current === ROUTES.qr) {
    renderQrIndex();
    return;
  }
  if (current === ROUTES.qrSubmit) {
    renderSingleQrPage('submit');
    return;
  }
  if (current === ROUTES.qrVote) {
    renderSingleQrPage('vote');
    return;
  }
  renderHome();
}

void start();
